/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AuthImage } from '../components/AuthImage';
import axios from 'axios';
import {
  Plus, Trash2, Download, Archive, Search, X,
  LayoutGrid, List, Columns, ImageIcon,
  BookOpen, Layers, ClipboardList,
  Film, Camera, AlertTriangle, Clock, Eye, MapPin,
  GripVertical, Check, Edit2, UserPlus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ImageUploadButton, LightboxThumb } from '../components/ImageUpload';
import LookAndFeelGallery from '../components/LookAndFeelGallery';
import ArtAIGenerateModal from '../components/ArtAIGenerateModal';
import PDFPreviewDialog from '../components/PDFPreviewDialog';
import { LanguageDownloadDialog } from '../components/LanguageDownloadDialog';
import DepartmentTasksPanel from '../components/DepartmentTasksPanel';
import ArtByScene from './art/ArtByScene';
import ArtByLocation from './art/ArtByLocation';
import ArtAssets from './art/ArtAssets';
import ArtByCharacter from './art/ArtByCharacter';
import ArtRequirements from './art/ArtRequirements';
import ArtExportDropdown from './art/ArtExportDropdown';
import { AssetActionsProvider } from './art/AssetActionsContext';
import { RequirementActionsProvider } from './art/RequirementActionsContext';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): LocationPropsTab
// was wired for a Locations sub-tab that was dropped. Restore or delete.
import { LocationPropsTab } from '../components/LocationPropsTab';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): ImageThumbStrip
// was wired for asset gallery strips; callsite dropped. Restore or delete.
import { ImageThumbStrip } from '../components/ImageUpload';
import { toast } from 'sonner';
import { MLText } from '../components/MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPT = 'ART';

const ART_CATEGORIES = [
  { value: 'action_prop', label: 'Action Prop' },
  { value: 'set_dressing', label: 'Set Dressing' },
  { value: 'weapon', label: 'Weapon' },
  { value: 'hero_prop', label: 'Hero Prop' },
  { value: 'set_piece', label: 'Set Piece' },
  { value: 'decoration', label: 'Decoration' },
  { value: 'picture_vehicle', label: 'Picture Vehicle' },
  { value: 'other', label: 'Other' },
];
const SOURCES = ['purchase', 'rent', 'make', 'borrow', 'other'];
const BLANK_ASSET = { description: '', asset_sub_type: 'other', source: 'purchase', notes: '', sceneIds: [] };

const STATUS_CONFIG = {
  locked:      { label: 'Locked',      color: '#4ade80', bg: 'rgba(74,222,128,.1)',   border: 'rgba(74,222,128,.25)' },
  'in-review': { label: 'In Review',   color: '#fbbf24', bg: 'rgba(251,191,36,.1)',   border: 'rgba(251,191,36,.25)' },
  flagged:     { label: 'Flagged',     color: '#f87171', bg: 'rgba(248,113,113,.1)',  border: 'rgba(248,113,113,.25)' },
  unreviewed:  { label: 'Unreviewed',  color: '#6b7280', bg: 'rgba(107,114,128,.08)', border: 'rgba(107,114,128,.2)' },
};

const STATUS_CYCLE = ['unreviewed', 'in-review', 'locked'];

// ── Sidebar ─────────────────────────────────────────────────────────────────
function ArtSidebar({ section, setSection, onAddAsset, onBulkAdd, recycledCount, onRecycleBin, onAIGenerate }) {
  const navItems = [
    { key: 'overview',   label: 'Overview',            icon: LayoutGrid },
    { key: 'scenes',     label: 'Scenes',               icon: Film },
    { key: 'locations',  label: 'Location Props',       icon: Camera },
    { key: 'gallery',    label: 'Look & Feel',          icon: ImageIcon },
    { key: 'assets',     label: 'Assets',               icon: Layers },
    { key: 'library',    label: 'Set Dressing Library', icon: BookOpen },
    { key: 'tasks',      label: 'Tasks',                icon: ClipboardList },
    { key: 'continuity', label: 'Continuity',           icon: Eye },
    { key: 'reports',    label: 'Reports',              icon: Columns },
  ];
  const quickActions = [
    { label: '+ Add Prop',         onClick: onAddAsset,  testid: 'qa-add-prop' },
    { label: '+ Add Set Dressing', onClick: onBulkAdd,   testid: 'qa-add-setdress' },
    { label: '+ Add Vehicle',      onClick: onAddAsset,  testid: 'qa-add-vehicle' },
    // Phase 12a Item 4 — orphan Art buttons wired to By Location tab.
    // These fire `art:switch-tab` (installed in ArtDepartmentPage) with
    // an `intent` string ArtByLocation listens for on mount.
    { label: '+ Add Mood Board', onClick: () => window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'by_location', intent: 'mood_board' } })), testid: 'qa-add-moodboard' },
    { label: '⬆ Upload Ref Photo', onClick: () => window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'by_location', intent: 'upload_photo' } })), testid: 'qa-upload-ref' },
    { label: '✦ AI Generate Props', onClick: onAIGenerate || (() => toast.info('AI Props coming soon')), testid: 'art-ai-generate-sidebar-btn' },
  ];
  return (
    <div className="flex flex-col flex-shrink-0 overflow-y-auto" style={{ width: 192, background: '#0a0c10', borderRight: '1px solid var(--cb-border)' }}>
      <div className="px-3 pt-3 pb-1">
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-border)', paddingLeft: 2, paddingBottom: 4 }}>Art Department</p>
        {navItems.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSection(key)}
            data-testid={`art-nav-${key}`}
            className="w-full text-left flex items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors mb-0.5"
            style={{
              color: section === key ? 'var(--cb-accent)' : '#777',
              background: section === key ? 'rgba(245,166,35,.07)' : 'transparent',
              borderLeft: section === key ? '2px solid var(--cb-accent)' : '2px solid transparent',
            }}
          >
            <Icon size={12} style={{ flexShrink: 0, opacity: section === key ? 1 : .5 }} />
            <span style={{ fontWeight: section === key ? 600 : 400 }}>{label}</span>
          </button>
        ))}
      </div>
      <div className="px-3 py-2 mt-1" style={{ borderTop: '1px solid var(--cb-border)' }}>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-border)', paddingLeft: 2, paddingBottom: 4 }}>Quick Actions</p>
        {quickActions.map(({ label, onClick, testid }) => (
          <button key={testid} onClick={onClick} data-testid={testid}
            className="w-full text-left px-2 py-1 text-xs rounded transition-colors mb-0.5 hover:text-amber-400"
            style={{ color: 'var(--cb-text-dim)' }}>
            {label}
          </button>
        ))}
        <button onClick={onRecycleBin} data-testid="recycle-bin-btn"
          className="w-full text-left px-2 py-1 text-xs rounded transition-colors mt-1 hover:text-red-400 flex items-center gap-1.5"
          style={{ color: 'var(--cb-text-dim)' }}>
          <Archive size={10} />
          Recycle Bin {recycledCount > 0 && <span style={{ background: 'rgba(248,113,113,.15)', color: '#f87171', padding: '0 4px', borderRadius: 3, fontSize: 9 }}>{recycledCount}</span>}
        </button>
      </div>
    </div>
  );
}

// ── Top header ───────────────────────────────────────────────────────────────
function ArtHeader({ scenes, totalProps, totalSetDressing, totalVehicles, section, setSection, onExport, onExportPdf }) {
  const subTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'scenes',   label: 'Scenes' },
    { key: 'locations',label: 'Locations' },
    { key: 'gallery',  label: 'References' },
    { key: 'assets',   label: 'Assets' },
  ];
  return (
    <div style={{ borderBottom: '1px solid var(--cb-border)', padding: '12px 20px 0', flexShrink: 0 }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ color: 'var(--cb-text)' }}>Art Department</h1>
          <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginTop: 2 }}>Manage props, set dressing, locations, mood boards and look & feel.</p>
        </div>
        <div className="flex items-center gap-4">
          {[
            { num: scenes.length, label: 'Scenes' },
            { num: totalProps,     label: 'Props' },
            { num: totalSetDressing, label: 'Set Dressing' },
            { num: totalVehicles,  label: 'Vehicles' },
          ].map(({ num, label }) => (
            <div key={label} className="text-center">
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--cb-accent)', lineHeight: 1 }}>{num}</div>
              <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--cb-text-dim)', marginTop: 1 }}>{label}</div>
            </div>
          ))}
          <div className="flex gap-2 ml-2">
            <Button size="sm" variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8 text-xs" data-testid="export-list-btn" onClick={onExport}>
              <Download className="h-3.5 w-3.5 mr-1" /> Export List
            </Button>
            <Button size="sm" variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8 text-xs" data-testid="art-export-pdf-btn" onClick={onExportPdf} title="Preview & download Art breakdown PDF">
              <Download className="h-3.5 w-3.5 mr-1" /> Export PDF
            </Button>
            <Button size="sm" className="h-8 text-xs font-bold text-black" style={{ background: 'var(--cb-accent)' }} data-testid="header-add-asset-btn"
              onClick={() => window.__openArtAssetModal?.(null)}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Asset
            </Button>
          </div>
        </div>
      </div>
      <div className="flex">
        {subTabs.map(({ key, label }) => (
          <button key={key} onClick={() => setSection(key)}
            className="px-4 py-2 text-xs transition-colors"
            style={{ color: section === key ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderBottom: section === key ? '2px solid var(--cb-accent)' : '2px solid transparent' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, onClick }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unreviewed;
  return (
    <button onClick={onClick}
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </button>
  );
}

// ── Scene card ────────────────────────────────────────────────────────────────
function SceneCard({ scene, status, onSelectScene, onCycleStatus, onBulkAdd, onAddAsset, onAIGenerate, isSelected, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const props    = scene.breakdown_items?.PROP || [];
  const dressing = scene.breakdown_items?.ART_SET_DRESSING || [];
  const vehicles = scene.breakdown_items?.PICTURE_VEHICLE || [];
  const allTags  = [...props.slice(0, 2), ...dressing.slice(0, 1)];
  const extra    = (props.length + dressing.length + vehicles.length) - allTags.length;
  const imageCount = [...props, ...dressing, ...vehicles].reduce((a, i) => a + (i.image_paths?.length || 0), 0);

  return (
    <div
      onClick={() => onSelectScene(scene)}
      data-testid={`art-scene-${scene.scene_number ?? 'unnumbered'}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={e => { e.preventDefault(); onDragOver(); }}
      onDrop={e => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
      className="flex flex-col cursor-pointer rounded-lg overflow-hidden transition-all"
      style={{
        background: 'var(--cb-surface)',
        border: isSelected ? '1px solid var(--cb-accent)' : isDragOver ? '1px dashed var(--cb-accent)' : '1px solid var(--cb-border)',
        boxShadow: isSelected ? '0 0 0 1px rgba(245,166,35,.2)' : 'none',
        opacity: isDragOver ? 0.7 : 1,
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(245,166,35,.35)'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--cb-border)'; }}
    >
      {/* Drag handle row */}
      <div style={{ padding: '3px 12px 0', display: 'flex', justifyContent: 'center', cursor: 'grab', opacity: 0.25 }}
        onClick={e => e.stopPropagation()}>
        <GripVertical size={12} style={{ color: 'var(--cb-text-sec)' }} />
      </div>
      {/* Card top */}
      <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid var(--cb-border)' }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--cb-accent)', background: 'rgba(245,166,35,.12)', padding: '2px 7px', borderRadius: 4 }}>
              SC.{formatSceneNumber(scene.scene_number)}
            </span>
            <span style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>
              {(scene.location_type || 'INT').toUpperCase()} · {(scene.time_of_day || 'DAY').toUpperCase()}
            </span>
          </div>
          <StatusBadge status={status} onClick={e => { e.stopPropagation(); onCycleStatus(scene.id); }} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cb-text)', lineHeight: 1.3, marginBottom: 2 }}>{scene.scene_header}</div>
        <div style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>Day {formatSceneNumber(scene.scene_number)}</div>
      </div>

      {/* Counts */}
      <div className="flex gap-3" style={{ padding: '7px 12px', borderBottom: '1px solid var(--cb-border)' }}>
        {[['#60a5fa', 'Props', props.length], ['#34d399', 'Set Dressing', dressing.length], ['#fb923c', 'Vehicles', vehicles.length]].map(([color, label, count]) => (
          <div key={label} className="flex items-center gap-1" style={{ fontSize: 10, color: 'var(--cb-text-sec)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {label} <span style={{ fontWeight: 700, color: 'var(--cb-text-sec)' }}>{count}</span>
          </div>
        ))}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1" style={{ padding: '7px 12px', minHeight: 36 }}>
        {props.slice(0, 2).map(i => (
          <span key={i.id} style={{ background: 'rgba(96,165,250,.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,.2)', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 500 }}>
            <MLText as="span">{getBreakdownItemLabel(i.description, 'original').slice(0, 24)}</MLText>
          </span>
        ))}
        {dressing.slice(0, 1).map(i => (
          <span key={i.id} style={{ background: 'rgba(52,211,153,.1)', color: '#34d399', border: '1px solid rgba(52,211,153,.2)', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 500 }}>
            <MLText as="span">{getBreakdownItemLabel(i.description, 'original').slice(0, 24)}</MLText>
          </span>
        ))}
        {extra > 0 && <span style={{ background: 'var(--cb-border)', color: 'var(--cb-text-dim)', border: '1px solid var(--cb-border)', padding: '2px 7px', borderRadius: 4, fontSize: 10 }}>+{extra}</span>}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between" style={{ padding: '7px 12px', borderTop: '1px solid var(--cb-border)', fontSize: 10, color: 'var(--cb-text-dim)' }}>
        <span>📷 {imageCount} Images</span>
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { onBulkAdd(scene); }}
            data-testid={`bulk-add-${scene.scene_number ?? 'unnumbered'}`}
            className="px-2 py-0.5 rounded text-xs hover:text-amber-400 transition-colors"
            style={{ border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}>
            Bulk Add
          </button>
          <button
            onClick={() => onAIGenerate && onAIGenerate(scene)}
            data-testid={`art-ai-generate-scene-btn-${scene.scene_number ?? 'unnumbered'}`}
            className="px-2 py-0.5 rounded text-xs hover:text-amber-400 transition-colors flex items-center gap-1"
            style={{ border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}>
            ✦ AI
          </button>
          <button
            onClick={() => onAddAsset(scene)}
            data-testid={`add-asset-${scene.scene_number ?? 'unnumbered'}`}
            className="px-2 py-0.5 rounded text-xs font-semibold text-black"
            style={{ background: 'var(--cb-accent)' }}>
            + Asset
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Right panel ───────────────────────────────────────────────────────────────
function RightPanel({ scene, status, onClose, onCycleStatus, onDelete, fetchAll, token, projectId, onUpdateScene, onAddProp, onAddSetDressing, onBulkAdd, onAIGenerate }) {
  const [rpTab, setRpTab] = useState('overview');
  const [editingLoc, setEditingLoc] = useState(false);
  const [locVal, setLocVal] = useState(scene.location_name || '');
  const [addingChar, setAddingChar] = useState(false);
  const [newChar, setNewChar] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const saveLoc = async () => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/scenes/${scene.id}`, { location_name: locVal.trim() }, { headers });
      setEditingLoc(false);
      fetchAll();
      toast.success('Location updated');
    } catch { toast.error('Failed'); }
  };

  const saveChars = async (chars) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/scenes/${scene.id}`, { characters_in_scene: chars }, { headers });
      fetchAll();
    } catch { toast.error('Failed to update cast'); }
  };

  const removeChar = (name) => saveChars((scene.characters_in_scene || []).filter(c => c !== name));
  const addChar = async () => {
    if (!newChar.trim()) return;
    await saveChars([...(scene.characters_in_scene || []), newChar.trim()]);
    setNewChar(''); setAddingChar(false);
  };

  // sync loc val if scene changes
  useEffect(() => { setLocVal(scene.location_name || ''); }, [scene.id, scene.location_name]);

  if (!scene) return null;
  const props    = scene.breakdown_items?.PROP || [];
  const dressing = scene.breakdown_items?.ART_SET_DRESSING || [];
  const vehicles = scene.breakdown_items?.PICTURE_VEHICLE || [];

  const buildGal = items => {
    const images = []; const startIdx = {};
    (items || []).forEach(it => {
      startIdx[it.id] = images.length;
      (it.image_paths || []).forEach((p, i) => images.push({ path: p, alt: `${it.description || ''} ${i + 1}` }));
    });
    return { images, startIdx };
  };
  const propsGal = buildGal(props);
  const dresGal  = buildGal(dressing);
  const vegGal   = buildGal(vehicles);

  const ItemRow = ({ item, gal }) => {
    const [expanded, setExpanded] = useState(false);
    const photoCount = item.image_paths?.length || 0;
    const handleMultiUpload = async (files) => {
      const flist = Array.from(files || []);
      for (const f of flist) {
        // eslint-disable-next-line no-await-in-loop
        try {
          const fd = new FormData();
          fd.append('file', f);
          // eslint-disable-next-line no-await-in-loop
          await axios.post(`${API}/items/${item.id}/images/upload`, fd, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (e) { console.error('art-photo-upload:', e); toast.error(`Upload failed for ${f.name}`); }
      }
      fetchAll();
    };
    const handleDeletePhoto = async (path) => {
      try {
        await axios.delete(`${API}/items/${item.id}/images?path=${encodeURIComponent(path)}`,
          { headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
      } catch (e) { toast.error('Failed to delete photo'); }
    };
    return (
      <div key={item.id} data-testid={`asset-row-${item.id}`} style={{ borderBottom: '1px solid #1a1c24' }}>
        <div className="flex items-center gap-2 py-1.5" data-testid={`art-item-${item.id}`}>
          <button onClick={() => setExpanded(v => !v)} data-testid={`asset-row-expand-${item.id}`}
            className="text-xs px-1 hover:text-amber-400 transition-colors"
            style={{ color: 'var(--cb-text-dim)' }}>
            {expanded ? '▾' : '▸'}
          </button>
          <div className="flex-shrink-0 rounded overflow-hidden" style={{ width: 28, height: 28, background: 'var(--cb-border)' }}>
            {item.image_paths?.length > 0
              ? <LightboxThumb paths={item.image_paths} className="w-full h-full object-cover" galleryImages={gal.images} galleryStartIndex={gal.startIdx[item.id] || 0} />
              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--cb-border)' }}>P</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 11, color: 'var(--cb-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
            <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{item.asset_sub_type || item.source || ''}</div>
          </div>
          <div className="flex items-center gap-0.5">
            <ImageUploadButton itemId={item.id} onUploaded={fetchAll} />
            <button onClick={() => onDelete(item.id)} className="p-1 hover:text-red-400 transition-colors" style={{ color: 'var(--cb-text-dim)' }}>
              <Trash2 size={11} />
            </button>
          </div>
        </div>
        {expanded && (
          <div className="pl-7 pr-2 pb-3 pt-1" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--cb-text-dim)' }}>
                Photos <span style={{ color: 'var(--cb-text)' }}>· {photoCount} photo{photoCount === 1 ? '' : 's'}</span>
              </div>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('art:ai-generate', { detail: { sceneId: item.scene_id, prompt: `Similar to: ${item.description}` } }))}
                className="text-[10px] px-2 py-0.5 rounded hover:text-amber-400 transition-colors"
                style={{ border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}
                title="Generate similar via AI"
              >
                ✦ Generate similar
              </button>
            </div>
            <label
              className="block rounded border border-dashed text-center py-3 cursor-pointer text-xs hover:bg-white/5 transition-colors"
              style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-dim)' }}
              data-testid={`asset-photos-dropzone-${item.id}`}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleMultiUpload(e.dataTransfer.files); }}
            >
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => handleMultiUpload(e.target.files)} />
              Drag &amp; drop photos here, or click to browse
            </label>
            {photoCount > 0 && (
              <div className="grid grid-cols-6 gap-1.5 mt-2">
                {item.image_paths.map((p, i) => (
                  <div key={i} className="relative group rounded overflow-hidden" style={{ aspectRatio: '1/1', background: 'var(--cb-border)' }}>
                    <LightboxThumb paths={[p]} className="w-full h-full object-cover" galleryImages={[{ src: p }]} galleryStartIndex={0} />
                    <button onClick={() => handleDeletePhoto(p)}
                      className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete photo">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const rpTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'props',    label: `Props (${props.length})` },
    { key: 'setdress', label: `Set Dressing (${dressing.length})` },
    { key: 'vehicles', label: `Vehicles (${vehicles.length})` },
    { key: 'images',   label: `Images (${props.length + dressing.length + vehicles.length > 0 ? [...props, ...dressing, ...vehicles].reduce((a, i) => a + (i.image_paths?.length || 0), 0) : 0})` },
  ];

  return (
    <div className="flex flex-col flex-shrink-0 overflow-hidden" style={{ width: 320, borderLeft: '1px solid var(--cb-border)', background: 'var(--cb-bg)' }}>
      {/* RP Header */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0 }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--cb-accent)' }}>SC.{formatSceneNumber(scene.scene_number)}</span>
            <span style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>{(scene.location_type || 'INT').toUpperCase()} · {(scene.time_of_day || 'DAY').toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={status} onClick={() => onCycleStatus(scene.id)} />
            <button onClick={onClose} style={{ width: 20, height: 20, borderRadius: 4, background: 'var(--cb-border)', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={11} />
            </button>
          </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cb-text)', lineHeight: 1.2, marginBottom: 2 }}>{scene.scene_header}</div>
        <div style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>Day {formatSceneNumber(scene.scene_number)} · {scene.location_name || 'Unknown Location'}</div>
      </div>

      {/* RP Tabs */}
      <div className="flex overflow-x-auto" style={{ borderBottom: '1px solid var(--cb-border)', flexShrink: 0 }}>
        {rpTabs.map(({ key, label }) => (
          <button key={key} onClick={() => setRpTab(key)}
            style={{ padding: '7px 10px', fontSize: 10, color: rpTab === key ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderBottom: rpTab === key ? '2px solid var(--cb-accent)' : '2px solid transparent', whiteSpace: 'nowrap', background: 'none', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* RP Body */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '12px 14px' }}>
        {rpTab === 'overview' && (
          <>
            {/* Editable Location */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Location</p>
                <button onClick={() => setEditingLoc(true)} style={{ fontSize: 9, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  <Edit2 size={10} />
                </button>
              </div>
              {editingLoc ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={locVal} onChange={e => setLocVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveLoc(); if (e.key === 'Escape') setEditingLoc(false); }}
                    autoFocus
                    style={{ flex: 1, background: 'var(--cb-surface)', border: '1px solid var(--cb-accent)', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: 'var(--cb-text)', outline: 'none' }} />
                  <button onClick={saveLoc} style={{ padding: '4px 8px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                    <Check size={11} />
                  </button>
                  <button onClick={() => setEditingLoc(false)} style={{ padding: '4px 8px', background: 'var(--cb-border)', color: 'var(--cb-text-sec)', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>
                    <X size={11} />
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--cb-text-sec)', padding: '6px 8px', background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 4 }}>
                  {scene.location_name || <span style={{ color: 'var(--cb-border)' }}>No location set</span>}
                </div>
              )}
            </div>

            {/* Editable Cast */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Cast in Scene</p>
                <button onClick={() => setAddingChar(true)} style={{ fontSize: 9, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <UserPlus size={10} /> Add
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: addingChar ? 8 : 0 }}>
                {(scene.characters_in_scene || []).length === 0 && !addingChar && (
                  <span style={{ fontSize: 11, color: 'var(--cb-border)', fontStyle: 'italic' }}>No cast assigned</span>
                )}
                {(scene.characters_in_scene || []).map(c => (
                  <span key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--cb-border)', border: '1px solid var(--cb-border)', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: 'var(--cb-text-sec)' }}>
                    {c}
                    <button onClick={() => removeChar(c)} style={{ background: 'none', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                      <X size={9} />
                    </button>
                  </span>
                ))}
              </div>
              {addingChar && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={newChar} onChange={e => setNewChar(e.target.value)} placeholder="Character name…"
                    onKeyDown={e => { if (e.key === 'Enter') addChar(); if (e.key === 'Escape') { setAddingChar(false); setNewChar(''); } }}
                    autoFocus
                    style={{ flex: 1, background: 'var(--cb-surface)', border: '1px solid var(--cb-accent)', borderRadius: 4, padding: '4px 8px', fontSize: 12, color: 'var(--cb-text)', outline: 'none' }} />
                  <button onClick={addChar} style={{ padding: '4px 8px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                    <Check size={11} />
                  </button>
                  <button onClick={() => { setAddingChar(false); setNewChar(''); }} style={{ padding: '4px 8px', background: 'var(--cb-border)', color: 'var(--cb-text-sec)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    <X size={11} />
                  </button>
                </div>
              )}
            </div>

            {/* Scene Overview grid */}
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', marginBottom: 8, fontWeight: 700 }}>Scene Overview</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              {[['Props', props.length, '#60a5fa'], ['Set Dressing', dressing.length, '#34d399'], ['Vehicles', vehicles.length, '#fb923c'], ['Status', STATUS_CONFIG[status]?.label || 'Unreviewed', STATUS_CONFIG[status]?.color || '#6b7280'], ['Images', [...props, ...dressing, ...vehicles].reduce((a, i) => a + (i.image_paths?.length || 0), 0), '#a78bfa'], ['INT/EXT', (scene.location_type || 'INT'), '#94a3b8']].map(([label, val, color]) => (
                <div key={label} style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--cb-text-dim)', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', marginBottom: 8, fontWeight: 700 }}>Quick Actions</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <button data-testid={`rp-add-prop-${scene.scene_number ?? 'unnumbered'}`}
                onClick={() => onAddProp && onAddProp(scene)}
                style={{ padding: '6px 8px', border: '1px solid rgba(245,166,35,.3)', borderRadius: 6, fontSize: 10, color: 'var(--cb-accent)', background: 'rgba(245,166,35,.08)', cursor: 'pointer', textAlign: 'center' }}>
                + Add Prop
              </button>
              <button data-testid={`rp-add-setdress-${scene.scene_number ?? 'unnumbered'}`}
                onClick={() => onAddSetDressing && onAddSetDressing(scene)}
                style={{ padding: '6px 8px', border: '1px solid rgba(245,166,35,.3)', borderRadius: 6, fontSize: 10, color: 'var(--cb-accent)', background: 'rgba(245,166,35,.08)', cursor: 'pointer', textAlign: 'center' }}>
                + Add Set Dressing
              </button>
              <button data-testid={`rp-upload-${scene.scene_number ?? 'unnumbered'}`}
                onClick={() => {
                  const inp = document.createElement('input');
                  inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = false;
                  inp.onchange = async () => {
                    const f = inp.files?.[0]; if (!f) return;
                    try {
                      const fd = new FormData(); fd.append('file', f);
                      const qs = new URLSearchParams({ department: 'ART', location_name: scene.location_name || 'Unknown' }).toString();
                      const API = process.env.REACT_APP_BACKEND_URL + '/api';
                      const res = await fetch(`${API}/projects/${projectId}/gallery?${qs}`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: fd,
                      });
                      if (!res.ok) throw new Error(await res.text());
                      toast.success('Image uploaded to scene gallery');
                      fetchAll && fetchAll();
                    } catch (e) { toast.error(`Upload failed: ${e.message?.slice(0,80) || e}`); }
                  };
                  inp.click();
                }}
                style={{ padding: '6px 8px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 10, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer', textAlign: 'center' }}>
                ⬆ Upload Image
              </button>
              <button data-testid={`rp-ai-${scene.scene_number ?? 'unnumbered'}`}
                onClick={() => onAIGenerate && onAIGenerate(scene)}
                style={{ padding: '6px 8px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 10, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer', textAlign: 'center' }}>
                ✦ AI Suggest Props
              </button>
              <button data-testid={`rp-bulk-${scene.scene_number ?? 'unnumbered'}`} onClick={() => onBulkAdd && onBulkAdd(scene)}
                style={{ gridColumn: 'span 2', padding: '6px 8px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 10, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer', textAlign: 'center' }}>
                📋 Bulk Add Items
              </button>
            </div>
          </>
        )}

        {rpTab === 'props' && (
          <>
            {props.length === 0
              ? <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', marginTop: 24 }}>No props in this scene</p>
              : props.map(item => <ItemRow key={item.id} item={item} gal={propsGal} />)
            }
            <button
              onClick={() => onAddProp && onAddProp(scene)}
              style={{ marginTop: 10, width: '100%', padding: '7px', border: '1px dashed rgba(245,166,35,.35)', borderRadius: 6, fontSize: 10, color: 'var(--cb-accent)', background: 'rgba(245,166,35,.05)', cursor: 'pointer' }}>
              + Add Prop
            </button>
          </>
        )}
        {rpTab === 'setdress' && (
          <>
            {dressing.length === 0
              ? <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', marginTop: 24 }}>No set dressing items</p>
              : dressing.map(item => <ItemRow key={item.id} item={item} gal={dresGal} />)
            }
            <button
              onClick={() => onAddSetDressing && onAddSetDressing(scene)}
              style={{ marginTop: 10, width: '100%', padding: '7px', border: '1px dashed rgba(52,211,153,.35)', borderRadius: 6, fontSize: 10, color: '#34d399', background: 'rgba(52,211,153,.05)', cursor: 'pointer' }}>
              + Add Set Dressing
            </button>
          </>
        )}
        {rpTab === 'vehicles' && (
          vehicles.length === 0
            ? <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', marginTop: 24 }}>No picture vehicles</p>
            : vehicles.map(item => <ItemRow key={item.id} item={item} gal={vegGal} />)
        )}
        {rpTab === 'images' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {[...props, ...dressing, ...vehicles].flatMap(i => i.image_paths || []).map((p, idx) => (
              <div key={idx} style={{ aspectRatio: '1', borderRadius: 4, overflow: 'hidden', background: 'var(--cb-border)' }}>
                <AuthImage path={p} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
            {[...props, ...dressing, ...vehicles].flatMap(i => i.image_paths || []).length === 0 &&
              <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', marginTop: 24, gridColumn: 'span 3' }}>No images uploaded yet</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Overview Section ─────────────────────────────────────────────────────────
function OverviewSection({ scenes, statusMap, missingItems, setSection }) {
  const statusCounts = { locked: 0, 'in-review': 0, flagged: 0, unreviewed: 0 };
  scenes.forEach(s => { const st = statusMap[s.id] || 'unreviewed'; statusCounts[st] = (statusCounts[st] || 0) + 1; });
  const totalProps = scenes.reduce((a, s) => a + (s.breakdown_items?.PROP || []).length, 0);
  const totalSet   = scenes.reduce((a, s) => a + (s.breakdown_items?.ART_SET_DRESSING || []).length, 0);
  const totalVeh   = scenes.reduce((a, s) => a + (s.breakdown_items?.PICTURE_VEHICLE || []).length, 0);
  const uniqueLocs = [...new Set(scenes.map(s => s.location_name).filter(Boolean))];

  const C = { gap: 12, borderRadius: 8, background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', padding: '14px 16px' };
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        {[['Total Props', totalProps, 'var(--cb-accent)', `Across ${scenes.length} scenes`], ['Set Dressing', totalSet, '#34d399', 'Items catalogued'], ['Picture Vehicles', totalVeh, '#60a5fa', 'On-camera vehicles'], ['Locations', uniqueLocs.length, '#a78bfa', 'Unique locations']].map(([label, val, color, sub]) => (
          <div key={label} style={C}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: 10, color: 'var(--cb-text-dim)', marginTop: 4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Status row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        {[['locked', '🔒'], ['in-review', '👁'], ['flagged', '⚠'], ['unreviewed', '○']].map(([st, icon]) => {
          const cfg = STATUS_CONFIG[st];
          return (
            <div key={st} style={{ ...C, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setSection('scenes')}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{statusCounts[st] || 0}</div>
                <div style={{ fontSize: 10, color: 'var(--cb-text-dim)', marginTop: 2 }}>{cfg.label}</div>
                <div style={{ height: 3, background: 'var(--cb-border)', borderRadius: 2, marginTop: 7, overflow: 'hidden' }}>
                  <div style={{ width: `${scenes.length ? (statusCounts[st] || 0) / scenes.length * 100 : 0}%`, height: '100%', background: cfg.color, borderRadius: 2 }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Location Quick Access */}
      {uniqueLocs.length > 0 && (
        <div style={{ ...C, padding: 0, marginBottom: 18 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--cb-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cb-text-sec)' }}>Location Quick Access</span>
            <span style={{ fontSize: 10, color: 'var(--cb-accent)', cursor: 'pointer' }} onClick={() => setSection('locations')}>View All →</span>
          </div>
          <div style={{ display: 'flex', gap: 10, padding: '12px 14px', overflowX: 'auto' }}>
            {uniqueLocs.slice(0, 6).map(loc => {
              const locScenes = scenes.filter(s => s.location_name === loc);
              const locProps = locScenes.reduce((a, s) => a + (s.breakdown_items?.PROP || []).length, 0);
              return (
                <div key={loc} style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden', minWidth: 140, flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => setSection('locations')}>
                  <div style={{ height: 60, background: 'linear-gradient(135deg, #1a1510, #2a2018)', display: 'flex', alignItems: 'flex-end', padding: '4px 6px' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(245,166,35,.8)', color: 'var(--cb-bg)', padding: '1px 5px', borderRadius: 2 }}>Active</span>
                  </div>
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-text)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{loc}</div>
                    <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>{locScenes.length} Scenes · {locProps} Props</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending Tasks + Missing Items */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Pending Tasks */}
        <div style={{ ...C, padding: 0 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--cb-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cb-text-sec)' }}>Pending Tasks</span>
            <span style={{ fontSize: 10, color: 'var(--cb-accent)', cursor: 'pointer' }} onClick={() => setSection('tasks')}>View All →</span>
          </div>
          <div style={{ padding: '4px 14px 8px' }}>
            {missingItems.slice(0, 4).map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                <div style={{ width: 14, height: 14, border: '1px solid var(--cb-border)', borderRadius: 3, flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--cb-text-sec)', lineHeight: 1.4 }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                  <div style={{ fontSize: 10, color: 'var(--cb-text-dim)', marginTop: 1 }}>Sc. {item.scene?.scene_number} — {item.scene?.location_name || 'Unknown'}</div>
                </div>
                <span style={{ background: 'rgba(248,113,113,.12)', color: '#f87171', padding: '2px 7px', borderRadius: 3, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap' }}>Review</span>
              </div>
            ))}
            {missingItems.length === 0 && <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', padding: '16px 0' }}>No pending tasks</p>}
          </div>
        </div>

        {/* Missing / Flagged */}
        <div style={{ ...C, padding: 0 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--cb-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cb-text-sec)' }}>Missing / Flagged Items</span>
            <span style={{ fontSize: 10, color: 'var(--cb-accent)', cursor: 'pointer' }} onClick={() => setSection('scenes')}>View All →</span>
          </div>
          <div style={{ padding: '4px 14px 8px' }}>
            {missingItems.slice(0, 4).map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                <AlertTriangle size={11} style={{ color: '#f87171', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--cb-text-sec)' }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                  <div style={{ fontSize: 10, color: 'var(--cb-text-dim)' }}>Sc. {item.scene?.scene_number}</div>
                </div>
                <span style={{ background: 'rgba(248,113,113,.12)', color: '#f87171', padding: '2px 7px', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>Missing</span>
              </div>
            ))}
            {missingItems.length === 0 && <p style={{ fontSize: 11, color: 'var(--cb-border)', textAlign: 'center', padding: '16px 0' }}>No flagged items</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Scenes Section ────────────────────────────────────────────────────────────
function ScenesSection({ scenes, statusMap, cycleStatus, selectedScene, setSelectedScene, searchTerm, setSearchTerm, onBulkAdd, onAddAsset, onAddProp, onAddSetDressing, onDelete, fetchAll, token, projectId, onAIGenerate }) {
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');

  // ── Drag-and-drop reorder ────────────────────────────────────────────────
  const orderKey = `art_scene_order_${projectId}`;
  const [sceneOrder, setSceneOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(orderKey) || 'null'); } catch { return null; }
  });
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDrop = (targetId) => {
    if (!draggedId || draggedId === targetId) { setDraggedId(null); setDragOverId(null); return; }
    const base = sceneOrder || filtered.map(s => s.id);
    const from = base.indexOf(draggedId);
    const to   = base.indexOf(targetId);
    if (from === -1 || to === -1) { setDraggedId(null); return; }
    const next = [...base];
    next.splice(from, 1);
    next.splice(to, 0, draggedId);
    setSceneOrder(next);
    localStorage.setItem(orderKey, JSON.stringify(next));
    setDraggedId(null); setDragOverId(null);
  };

  const uniqueLocs = [...new Set(scenes.map(s => s.location_name).filter(Boolean))];
  const filtered = scenes.filter(s => {
    if (statusFilter !== 'all' && (statusMap[s.id] || 'unreviewed') !== statusFilter) return false;
    if (locationFilter !== 'all' && s.location_name !== locationFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase().trim();
      // Normalize scene_number ("19.0" → "19", suffix-preserving "16a")
      const sceneNumStr = formatSceneNumber(s.scene_number).toLowerCase();
      return sceneNumStr.includes(q) ||
             s.scene_header?.toLowerCase().includes(q) ||
             s.location_name?.toLowerCase().includes(q) ||
             s.artItems?.some(i => i.description?.toLowerCase().includes(q));
    }
    return true;
  });

  // Apply custom order
  const orderedScenes = sceneOrder
    ? [...filtered].sort((a, b) => {
        const ai = sceneOrder.indexOf(a.id), bi = sceneOrder.indexOf(b.id);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      })
    : filtered;

  // Columns: 2 when right panel open, 3 otherwise
  const cols = selectedScene ? 2 : (viewMode === 'grid' ? 3 : 1);

  return (
    <div className="flex flex-1 overflow-hidden">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap" style={{ padding: '10px 16px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', border: '1px solid var(--cb-border)', borderRadius: 6, overflow: 'hidden' }}>
            {[['grid', <LayoutGrid size={11} />], ['list', <List size={11} />]].map(([m, icon]) => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding: '5px 10px', background: viewMode === m ? 'var(--cb-border)' : 'transparent', color: viewMode === m ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderRight: '1px solid var(--cb-border)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {icon}
              </button>
            ))}
          </div>
          <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer' }}>
            <option value="all">All Locations</option>
            {uniqueLocs.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer' }}>
            <option value="all">All Status</option>
            <option value="locked">Locked</option>
            <option value="in-review">In Review</option>
            <option value="flagged">Flagged</option>
            <option value="unreviewed">Unreviewed</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--cb-border)', borderRadius: 6, padding: '4px 10px', background: 'var(--cb-surface)', flex: 1, maxWidth: 240 }}>
            <Search size={11} style={{ color: 'var(--cb-text-dim)', flexShrink: 0 }} />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search scenes, props..."
              style={{ background: 'none', border: 'none', outline: 'none', fontSize: 11, color: 'var(--cb-text-sec)', width: '100%' }}
              data-testid="art-search" />
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, transition: 'grid-template-columns .2s' }}>
            {orderedScenes.map(scene => (
              <SceneCard key={scene.id} scene={scene} status={statusMap[scene.id] || 'unreviewed'}
                onSelectScene={s => setSelectedScene(prev => prev?.id === s.id ? null : s)}
                onCycleStatus={cycleStatus} onBulkAdd={onBulkAdd} onAddAsset={onAddAsset} onAIGenerate={onAIGenerate}
                isSelected={selectedScene?.id === scene.id}
                isDragOver={dragOverId === scene.id}
                onDragStart={() => setDraggedId(scene.id)}
                onDragOver={() => setDragOverId(scene.id)}
                onDrop={() => handleDrop(scene.id)}
                onDragEnd={() => { setDraggedId(null); setDragOverId(null); }}
              />
            ))}
            {orderedScenes.length === 0 && (
              <div style={{ gridColumn: `span ${cols}`, textAlign: 'center', padding: 40, color: 'var(--cb-border)', fontSize: 12 }}>
                No scenes found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right panel */}
      {selectedScene && (
        <RightPanel scene={selectedScene} status={statusMap[selectedScene.id] || 'unreviewed'}
          onClose={() => setSelectedScene(null)} onCycleStatus={cycleStatus}
          onDelete={onDelete} fetchAll={fetchAll} token={token} projectId={projectId}
          onAddProp={onAddProp} onAddSetDressing={onAddSetDressing} onBulkAdd={onBulkAdd}
          onAIGenerate={onAIGenerate} />
      )}
    </div>
  );
}

// ── Placeholder for unbuilt sections ─────────────────────────────────────────
function PlaceholderSection({ label }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--cb-border)' }}>
      <Clock size={32} style={{ marginBottom: 12, opacity: .4 }} />
      <p style={{ fontSize: 14, fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>Coming soon</p>
    </div>
  );
}

// ── Location gradient palette ─────────────────────────────────────────────────
const LOC_GRADIENTS = [
  'linear-gradient(135deg,#1e1510,#3a2510)',
  'linear-gradient(135deg,#101520,#1a2a38)',
  'linear-gradient(135deg,#150f10,#2a1820)',
  'linear-gradient(135deg,#10181a,#183028)',
  'linear-gradient(135deg,#14121a,#241a30)',
  'linear-gradient(135deg,#1a1408,#302410)',
  'linear-gradient(135deg,#0e1418,#182028)',
  'linear-gradient(135deg,#181010,#281818)',
];
const locGrad = (name) => LOC_GRADIENTS[(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % LOC_GRADIENTS.length];

// ── Location Right Panel ──────────────────────────────────────────────────────
function LocationRightPanel({ loc, onClose, token, fetchAll }) {
  const [tab, setTab] = useState('overview');
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(loc.name);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const saveLocName = async () => {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed === loc.name) { setEditingName(false); return; }
    try {
      // Rename all scenes that have this location_name
      await Promise.all(loc.scenes.map(s =>
        axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/scenes/${s.id}`, { location_name: trimmed }, { headers })
      ));
      toast.success(`Location renamed to "${trimmed}"`);
      setEditingName(false);
      fetchAll();
    } catch { toast.error('Failed to rename location'); }
  };

  // Top props: sort by frequency
  const propCounts = {};
  loc.props.forEach(p => { propCounts[p.description] = (propCounts[p.description] || 0) + 1; });
  const topProps = Object.entries(propCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const setDressCounts = {};
  loc.setDressing.forEach(p => { setDressCounts[p.description] = (setDressCounts[p.description] || 0) + 1; });
  const topSetDress = Object.entries(setDressCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'props',    label: `Props (${loc.props.length})` },
    { key: 'setdress', label: `Set Dressing (${loc.setDressing.length})` },
    { key: 'moods',    label: 'Mood Board' },
    { key: 'refs',     label: 'References' },
    { key: 'notes',    label: 'Notes' },
  ];

  const S = (label, val) => (
    <div key={label} style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--cb-text-dim)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--cb-text)' }}>{val}</div>
    </div>
  );

  return (
    <div style={{ width: 440, borderLeft: '1px solid var(--cb-border)', background: 'var(--cb-bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {editingName ? (
              <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                <input value={nameVal} onChange={e => setNameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveLocName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  style={{ flex: 1, background: 'var(--cb-surface)', border: '1px solid var(--cb-accent)', borderRadius: 4, padding: '4px 8px', fontSize: 14, fontWeight: 700, color: 'var(--cb-text)', outline: 'none' }} />
                <button onClick={saveLocName} style={{ padding: '4px 8px', background: 'var(--cb-accent)', color: 'var(--cb-bg)', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}><Check size={11} /></button>
                <button onClick={() => setEditingName(false)} style={{ padding: '4px 8px', background: 'var(--cb-border)', color: 'var(--cb-text-sec)', border: 'none', borderRadius: 4, cursor: 'pointer' }}><X size={11} /></button>
              </div>
            ) : (
              <>
                <span style={{ fontSize: 16, fontWeight: 900, color: 'var(--cb-text)', letterSpacing: '-.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameVal}</span>
                <button onClick={() => setEditingName(true)} style={{ background: 'none', border: 'none', color: 'var(--cb-accent)', cursor: 'pointer', padding: 2, flexShrink: 0 }}><Edit2 size={11} /></button>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(74,222,128,.1)', color: '#4ade80', border: '1px solid rgba(74,222,128,.25)', flexShrink: 0 }}>Active</span>
              </>
            )}
          </div>
          <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--cb-border)', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, marginLeft: 8 }}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--cb-text-dim)' }}>{loc.scenes.length} Scenes &nbsp;·&nbsp; {loc.props.length} Props &nbsp;·&nbsp; {loc.setDressing.length} Set Dressing</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--cb-border)', overflowX: 'auto', flexShrink: 0 }}>
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: '7px 10px', fontSize: 10, color: tab === key ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderBottom: tab === key ? '2px solid var(--cb-accent)' : '2px solid transparent', whiteSpace: 'nowrap', background: 'none', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

        {tab === 'overview' && (
          <>
            {/* Stats mini grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              {S('Scenes', loc.scenes.length)}
              {S('Props', loc.props.length)}
              {S('Set Dressing', loc.setDressing.length)}
            </div>

            {/* Mood Board placeholder */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Mood Board</span>
                <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4 }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{ aspectRatio: '1', borderRadius: 4, background: LOC_GRADIENTS[i % LOC_GRADIENTS.length], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Camera size={10} style={{ color: 'var(--cb-border)' }} />
                  </div>
                ))}
              </div>
              <button onClick={() => window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'by_location', intent: 'mood_board' } }))}
                data-testid="qa-empty-add-moodboard"
                style={{ marginTop: 6, width: '100%', padding: '5px', border: '1px dashed var(--cb-border)', borderRadius: 4, fontSize: 10, color: 'var(--cb-text-dim)', background: 'none', cursor: 'pointer' }}>
                + Add Mood Board Image
              </button>
            </div>

            {/* Color Palette placeholder */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Color Palette</span>
                <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All</span>
              </div>
              {[`${loc.name} – Daylight`, `${loc.name} – Warm`, `${loc.name} – Evening`].map((label, pi) => {
                const colors = [
                  ['#3d2b1f','#c8a26e','#e8d5b0','#8b6347','#1a1008'],
                  ['#2a1810','#c87040','#e8a860','#8b4820','#1a0c04'],
                  ['#141018','#4a3060','#685090','#302848','#100c18'],
                ];
                return (
                  <div key={label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      {colors[pi].map((c, ci) => (
                        <div key={ci} style={{ width: 28, height: 18, borderRadius: 3, background: c }} />
                      ))}
                      <span style={{ fontSize: 9, color: 'var(--cb-text-dim)', marginLeft: 4 }}>+5</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--cb-text-sec)' }}>{label}</div>
                  </div>
                );
              })}
            </div>

            {/* Location References */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Location References</span>
                <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {['Exterior','Entrance','Interior','Wide Shot','Detail','Atmosphere'].map((label, ri) => (
                  <div key={label} style={{ borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ height: 52, background: LOC_GRADIENTS[(ri + 2) % LOC_GRADIENTS.length] }} />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,.6)', padding: '2px 4px', fontSize: 9, color: '#ccc' }}>{label}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => toast.info('Upload reference photos coming soon')}
                style={{ marginTop: 6, width: '100%', padding: '5px', border: '1px dashed var(--cb-border)', borderRadius: 4, fontSize: 10, color: 'var(--cb-text-dim)', background: 'none', cursor: 'pointer' }}>
                + Upload Reference Photo
              </button>
            </div>

            {/* Scenes at this location */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Scenes at this Location</span>
                <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All Scenes</span>
              </div>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
                {loc.scenes.slice(0, 4).map(s => (
                  <div key={s.id} style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 6, padding: '7px 10px', minWidth: 100, flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--cb-accent)', marginBottom: 2 }}>SC.{formatSceneNumber(s.scene_number)}</div>
                    <div style={{ fontSize: 9, color: 'var(--cb-text-sec)', marginBottom: 3 }}>{(s.location_type||'INT').toUpperCase()} · {(s.time_of_day||'DAY').toUpperCase()}</div>
                    <div style={{ fontSize: 10, color: 'var(--cb-text)', lineHeight: 1.3, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{s.scene_header}</div>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: 'rgba(74,222,128,.1)', color: '#4ade80' }}>Locked</span>
                  </div>
                ))}
                {loc.scenes.length > 4 && (
                  <div style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 6, minWidth: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--cb-text-dim)' }}>
                    +{loc.scenes.length - 4}
                  </div>
                )}
              </div>
            </div>

            {/* Top Props + Set Dressing */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Top Props</span>
                  <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All</span>
                </div>
                {topProps.length === 0
                  ? <p style={{ fontSize: 10, color: 'var(--cb-border)', fontStyle: 'italic' }}>No props</p>
                  : topProps.map(([desc, count]) => (
                    <div key={desc} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1a1c24', fontSize: 10 }}>
                      <span style={{ color: 'var(--cb-text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{desc}</span>
                      <span style={{ color: 'var(--cb-text-dim)', flexShrink: 0 }}>{count}</span>
                    </div>
                  ))}
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--cb-text-dim)', fontWeight: 700 }}>Top Set Dressing</span>
                  <span style={{ fontSize: 9, color: 'var(--cb-accent)', cursor: 'pointer' }}>View All</span>
                </div>
                {topSetDress.length === 0
                  ? <p style={{ fontSize: 10, color: 'var(--cb-border)', fontStyle: 'italic' }}>No set dressing</p>
                  : topSetDress.map(([desc, count]) => (
                    <div key={desc} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1a1c24', fontSize: 10 }}>
                      <span style={{ color: 'var(--cb-text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{desc}</span>
                      <span style={{ color: 'var(--cb-text-dim)', flexShrink: 0 }}>{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--cb-text-dim)', marginBottom: 8, fontWeight: 700 }}>Quick Actions</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { label: '+ Add Prop', amber: true, intent: 'add_prop' },
                  { label: '+ Add Set Dressing', amber: true, intent: 'add_set_dressing' },
                  { label: '+ Add Mood Board', amber: false, intent: 'mood_board' },
                  { label: '⬆ Upload Reference', amber: false, intent: 'upload_photo' },
                  { label: '✦ AI Suggest Props', amber: false, intent: 'ai_suggest' },
                ].map(({ label, amber, intent }, qi) => (
                  <button key={qi}
                    onClick={() => {
                      if (intent === 'ai_suggest') {
                        window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'by_scene', intent } }));
                      } else if (intent === 'add_prop' || intent === 'add_set_dressing') {
                        window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'assets', intent } }));
                      } else {
                        window.dispatchEvent(new CustomEvent('art:switch-tab', { detail: { tab: 'by_location', intent } }));
                      }
                    }}
                    data-testid={`qa-drawer-${intent}`}
                    style={{ padding: '6px 8px', border: amber ? '1px solid rgba(245,166,35,.3)' : '1px solid var(--cb-border)', borderRadius: 6, fontSize: 10, color: amber ? 'var(--cb-accent)' : 'var(--cb-text-sec)', background: amber ? 'rgba(245,166,35,.08)' : 'var(--cb-surface)', cursor: 'pointer', textAlign: 'center', gridColumn: qi === 4 ? 'span 2' : undefined }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {tab === 'props' && (
          <div>
            {loc.props.length === 0
              ? <p style={{ color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No props for this location</p>
              : loc.props.map((item, idx) => (
                <div key={`${item.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                  <div style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--cb-text-dim)', flexShrink: 0 }}>P</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--cb-text)' }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                    <div style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>Scene {item.scene_number || ''}</div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {tab === 'setdress' && (
          <div>
            {loc.setDressing.length === 0
              ? <p style={{ color: 'var(--cb-border)', fontSize: 12, textAlign: 'center', marginTop: 24 }}>No set dressing for this location</p>
              : loc.setDressing.map((item, idx) => (
                <div key={`${item.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #1a1c24' }}>
                  <div style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--cb-text-dim)', flexShrink: 0 }}>S</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--cb-text)' }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {(tab === 'moods' || tab === 'refs') && (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--cb-border)' }}>
            <Camera size={28} style={{ margin: '0 auto 10px', opacity: .3 }} />
            <p style={{ fontSize: 12, fontWeight: 600 }}>{tab === 'moods' ? 'No mood board images' : 'No reference photos'}</p>
            <p style={{ fontSize: 11, marginTop: 4 }}>Upload images to build your {tab === 'moods' ? 'mood board' : 'location references'}</p>
            <button onClick={() => toast.info('Upload coming soon')}
              style={{ marginTop: 14, padding: '6px 16px', border: '1px solid var(--cb-accent)', borderRadius: 6, fontSize: 11, color: 'var(--cb-accent)', background: 'none', cursor: 'pointer' }}>
              Upload Images
            </button>
          </div>
        )}

        {tab === 'notes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--cb-text-sec)' }}>Notes</span>
              <button style={{ fontSize: 10, color: 'var(--cb-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
            </div>
            <textarea
              placeholder="Add location notes, access instructions, special requirements..."
              style={{ width: '100%', minHeight: 120, background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 6, padding: '10px 12px', color: 'var(--cb-text-sec)', fontSize: 11, resize: 'vertical', outline: 'none' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Locations Section ─────────────────────────────────────────────────────────
function ArtAssetsSection({ scenes, onAIGenerate }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('ALL'); // ALL | PROP | ART_SET_DRESSING | PICTURE_VEHICLE

  const assets = useMemo(() => {
    const rows = [];
    (scenes || []).forEach(s => {
      const bi = s.breakdown_items || {};
      ['PROP', 'ART_SET_DRESSING', 'PICTURE_VEHICLE'].forEach(k => {
        (bi[k] || []).forEach(it => rows.push({
          ...it,
          _cat: k,
          _sceneNumber: s.scene_number,
          _sceneId: s.id,
          _location: s.location_name,
        }));
      });
    });
    // Group by (name.toLowerCase, category) → dedupe cross-scene occurrences,
    // aggregating scene numbers for the chip row.
    const map = new Map();
    for (const r of rows) {
      const key = `${r._cat}::${(r.description || r.name || '').trim().toLowerCase()}`;
      if (!map.has(key)) {
        map.set(key, { ...r, _scenes: [r._sceneNumber], _sceneIds: [r._sceneId] });
      } else {
        const cur = map.get(key);
        if (!cur._scenes.includes(r._sceneNumber)) cur._scenes.push(r._sceneNumber);
        if (!cur._sceneIds.includes(r._sceneId)) cur._sceneIds.push(r._sceneId);
      }
    }
    return Array.from(map.values());
  }, [scenes]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return assets.filter(a => {
      if (cat !== 'ALL' && a._cat !== cat) return false;
      if (!term) return true;
      const hay = `${a.description || ''} ${a.name || ''} ${a.sub_type || ''} ${(a._scenes || []).join(' ')} ${a._location || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [assets, q, cat]);

  const CAT_LABEL = { PROP: 'Prop', ART_SET_DRESSING: 'Set Dressing', PICTURE_VEHICLE: 'Vehicle' };
  const CAT_COLOR = { PROP: '#F5A623', ART_SET_DRESSING: '#22c55e', PICTURE_VEHICLE: '#60a5fa' };

  const counts = useMemo(() => ({
    ALL: assets.length,
    PROP: assets.filter(a => a._cat === 'PROP').length,
    ART_SET_DRESSING: assets.filter(a => a._cat === 'ART_SET_DRESSING').length,
    PICTURE_VEHICLE: assets.filter(a => a._cat === 'PICTURE_VEHICLE').length,
  }), [assets]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} data-testid="art-assets-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--cb-text)' }}>Art Assets</h2>
          <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginTop: 2 }}>All props, set dressing &amp; picture vehicles across the script — deduped and cross-referenced by scene.</p>
        </div>
        <Button size="sm" onClick={() => onAIGenerate && onAIGenerate()}
          style={{ background: 'var(--cb-accent)', color: '#000' }} data-testid="art-assets-ai-generate-btn">
          ✦ AI Suggest Props
        </Button>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }} data-testid="art-assets-filters">
        {['ALL', 'PROP', 'ART_SET_DRESSING', 'PICTURE_VEHICLE'].map(k => (
          <button key={k} onClick={() => setCat(k)}
            data-testid={`art-assets-tab-${k.toLowerCase()}`}
            style={{
              padding: '4px 12px', fontSize: 11, borderRadius: 999,
              border: `1px solid ${cat === k ? 'var(--cb-accent)' : 'var(--cb-border)'}`,
              background: cat === k ? 'rgba(245,166,35,.12)' : 'var(--cb-surface)',
              color: cat === k ? 'var(--cb-accent)' : 'var(--cb-text-sec)',
              cursor: 'pointer', fontWeight: 600,
            }}>
            {k === 'ALL' ? 'All' : CAT_LABEL[k]} <span style={{ opacity: 0.6 }}>({counts[k] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search props, set dressing, vehicles, scene #, or location…"
        data-testid="art-assets-search"
        style={{ width: '100%', padding: '8px 12px', marginBottom: 16, borderRadius: 6, border: '1px solid var(--cb-border)', background: 'var(--cb-surface)', color: 'var(--cb-text)', fontSize: 12 }}
      />

      {/* Grid */}
      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--cb-text-dim)', fontSize: 13 }}>
          {assets.length === 0 ? 'No assets yet. Break down some scenes to see props & set dressing here.' : 'No assets match your filters.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }} data-testid="art-assets-grid">
          {filtered.map((a) => {
            const thumb = (a.image_paths && a.image_paths[0]) || null;
            return (
              <div key={`${a._cat}-${(a.description || a.name || '').slice(0,40)}`}
                data-testid={`art-asset-card-${a.id}`}
                style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ aspectRatio: '4/3', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {thumb ? (
                    <AuthImage path={thumb}
                         alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 32, opacity: 0.15 }}>
                      {a._cat === 'PROP' ? '🎭' : a._cat === 'ART_SET_DRESSING' ? '🖼' : '🚗'}
                    </span>
                  )}
                </div>
                <div style={{ padding: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 3, background: `${CAT_COLOR[a._cat]}22`, color: CAT_COLOR[a._cat] }}>
                      {CAT_LABEL[a._cat]}
                    </span>
                    {a.sub_type && (
                      <span style={{ fontSize: 9, color: 'var(--cb-text-dim)', letterSpacing: '.06em' }}>
                        · {a.sub_type}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cb-text)', lineHeight: 1.3, marginBottom: 4 }}>
                    <MLText as="span">{a.description || a.name || 'Untitled'}</MLText>
                  </div>
                  {a._location && (
                    <div style={{ fontSize: 10, color: 'var(--cb-text-dim)', marginBottom: 6 }}>{a._location}</div>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(a._scenes || []).slice(0, 6).map(sn => (
                      <span key={sn} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text-sec)' }}>
                        S{sn}
                      </span>
                    ))}
                    {(a._scenes || []).length > 6 && (
                      <span style={{ fontSize: 9, color: 'var(--cb-text-dim)' }}>+{a._scenes.length - 6}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LocationsSection({ scenes, token, fetchAll, projectId }) {
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // Drag-and-drop reorder for location cards
  const locOrderKey = `art_loc_order_${projectId}`;
  const [locOrder, setLocOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(locOrderKey) || 'null'); } catch { return null; }
  });
  const [draggedLoc, setDraggedLoc] = useState(null);
  const [dragOverLoc, setDragOverLoc] = useState(null);

  const handleLocDrop = (targetName) => {
    if (!draggedLoc || draggedLoc === targetName) { setDraggedLoc(null); setDragOverLoc(null); return; }
    const base = locOrder || filtered.map(l => l.name);
    const from = base.indexOf(draggedLoc), to = base.indexOf(targetName);
    if (from === -1 || to === -1) return;
    const next = [...base]; next.splice(from, 1); next.splice(to, 0, draggedLoc);
    setLocOrder(next); localStorage.setItem(locOrderKey, JSON.stringify(next));
    setDraggedLoc(null); setDragOverLoc(null);
  };

  // Build location map from scenes
  const locationMap = {};
  scenes.forEach(scene => {
    const name = (scene.location_name || 'Unknown').trim();
    if (!locationMap[name]) locationMap[name] = { name, scenes: [], props: [], setDressing: [], vehicles: [] };
    locationMap[name].scenes.push(scene);
    locationMap[name].props.push(...(scene.breakdown_items?.PROP || []).map(p => ({ ...p, scene_number: scene.scene_number })));
    locationMap[name].setDressing.push(...(scene.breakdown_items?.ART_SET_DRESSING || []));
    locationMap[name].vehicles.push(...(scene.breakdown_items?.PICTURE_VEHICLE || []));
  });
  const allLocations = Object.values(locationMap).sort((a, b) => b.scenes.length - a.scenes.length);

  const filtered = allLocations.filter(loc => {
    if (search && !loc.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const orderedLocs = locOrder
    ? [...filtered].sort((a, b) => {
        const ai = locOrder.indexOf(a.name), bi = locOrder.indexOf(b.name);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1; if (bi === -1) return -1;
        return ai - bi;
      })
    : filtered;

  const locCols = selectedLoc ? 2 : (viewMode === 'grid' ? 3 : 1);

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      {/* Left: location grid */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--cb-border)', flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--cb-border)', borderRadius: 6, padding: '4px 10px', background: 'var(--cb-surface)', flex: 1, maxWidth: 220 }}>
            <Search size={11} style={{ color: 'var(--cb-text-dim)', flexShrink: 0 }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search locations..."
              style={{ background: 'none', border: 'none', outline: 'none', fontSize: 11, color: 'var(--cb-text-sec)', width: '100%' }} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--cb-text-dim)', cursor: 'pointer', fontSize: 13 }}>✕</button>}
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid var(--cb-border)', borderRadius: 6, fontSize: 11, color: 'var(--cb-text-sec)', background: 'var(--cb-surface)', cursor: 'pointer' }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
            <option value="in-review">In Review</option>
          </select>
          <div style={{ display: 'flex', border: '1px solid var(--cb-border)', borderRadius: 6, overflow: 'hidden', marginLeft: 'auto' }}>
            {[['grid', <LayoutGrid size={11} />], ['list', <List size={11} />]].map(([m, icon]) => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding: '5px 10px', background: viewMode === m ? 'var(--cb-border)' : 'transparent', color: viewMode === m ? 'var(--cb-accent)' : 'var(--cb-text-dim)', borderRight: '1px solid var(--cb-border)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* Location grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${locCols}, 1fr)`, gap: 12, transition: 'grid-template-columns .2s' }}>
            {orderedLocs.map(loc => (
              <div key={loc.name}
                draggable
                onDragStart={() => setDraggedLoc(loc.name)}
                onDragOver={e => { e.preventDefault(); setDragOverLoc(loc.name); }}
                onDrop={e => { e.preventDefault(); handleLocDrop(loc.name); }}
                onDragEnd={() => { setDraggedLoc(null); setDragOverLoc(null); }}
                onClick={() => setSelectedLoc(prev => prev?.name === loc.name ? null : loc)}
                data-testid={`loc-card-${loc.name.replace(/\s+/g, '-').toLowerCase()}`}
                style={{ background: 'var(--cb-surface)', border: selectedLoc?.name === loc.name ? '1px solid var(--cb-accent)' : dragOverLoc === loc.name ? '1px dashed var(--cb-accent)' : '1px solid var(--cb-border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', transition: 'border-color .15s', opacity: dragOverLoc === loc.name ? 0.7 : 1 }}
                onMouseEnter={e => { if (selectedLoc?.name !== loc.name) e.currentTarget.style.borderColor = 'rgba(245,166,35,.4)'; }}
                onMouseLeave={e => { if (selectedLoc?.name !== loc.name) e.currentTarget.style.borderColor = 'var(--cb-border)'; }}>
                {/* Photo */}
                <div style={{ height: 110, background: locGrad(loc.name), position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: 8 }}>
                  <div style={{ cursor: 'grab', opacity: 0.3 }} onClick={e => e.stopPropagation()}>
                    <GripVertical size={12} style={{ color: 'var(--cb-text)' }} />
                  </div>
                  {selectedLoc?.name === loc.name && (
                    <div style={{ position: 'absolute', top: 8, left: 28, width: 16, height: 16, borderRadius: '50%', background: 'var(--cb-accent)', border: '2px solid var(--cb-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>✓</div>
                  )}
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(74,222,128,.85)', color: 'var(--cb-bg)' }}>Active</span>
                  <div style={{ position: 'absolute', bottom: 10, left: 14, fontSize: 22, fontWeight: 900, color: 'rgba(255,255,255,.08)', letterSpacing: '-.04em', lineHeight: 1 }}>
                    {loc.name.slice(0, 2).toUpperCase()}
                  </div>
                </div>
                {/* Card body */}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--cb-text)', marginBottom: 3, letterSpacing: '-.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.name.toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 8 }}>{loc.scenes.length} Scenes &nbsp;·&nbsp; {loc.props.length} Props</div>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 0 }}>
                    {[['📋', 'Mood Board', 0], ['🔗', 'References', 0], ['🎨', 'Palette', 0]].map(([icon, label, count]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--cb-text-dim)' }}>
                        <span>{icon}</span><span style={{ color: 'var(--cb-text-sec)', fontWeight: 600 }}>{label}</span>
                        <span style={{ color: 'var(--cb-text-dim)', marginLeft: 2, fontWeight: 700 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* Add New Location card */}
            <div onClick={() => toast.info('Add Location coming soon')}
              style={{ background: 'var(--cb-surface)', border: '1px dashed var(--cb-border)', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: 'var(--cb-border)', transition: 'border-color .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cb-accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--cb-border)'}
              data-testid="add-new-location-btn">
              <div style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--cb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                <Plus size={14} style={{ color: 'var(--cb-text-dim)' }} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cb-text-dim)' }}>Add New Location</div>
              <div style={{ fontSize: 10, color: 'var(--cb-border)', marginTop: 3 }}>Create a new location and start building</div>
            </div>
          </div>

          {filtered.length === 0 && allLocations.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--cb-border)' }}>
              <MapPin size={28} style={{ margin: '0 auto 10px', opacity: .3 }} />
              <p style={{ fontSize: 13, fontWeight: 600 }}>No locations yet</p>
              <p style={{ fontSize: 11, marginTop: 4 }}>Locations are derived from scene data</p>
            </div>
          )}
        </div>
      </div>

      {/* Right panel */}
      {selectedLoc && <LocationRightPanel loc={selectedLoc} onClose={() => setSelectedLoc(null)} token={token} fetchAll={fetchAll} />}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ArtDepartmentPage() {
  const { projectId } = useParams();
  const { token } = useAuth();

  const [section, setSection] = useState('scenes');
  const [scenes, setScenes] = useState([]);
  const [allScenes, setAllScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScene, setSelectedScene] = useState(null);

  // Art status per scene — persisted to localStorage
  const [artStatusMap, setArtStatusMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`art_status_${projectId}`) || '{}'); } catch { return {}; }
  });

  // Dialogs
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkSceneIds, setBulkSceneIds] = useState([]);
  const [bulkText, setBulkText] = useState('');
  const [bulkCategory, setBulkCategory] = useState('ART_SET_DRESSING');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [dialogSceneId, setDialogSceneId] = useState(null);
  const [newAsset, setNewAsset] = useState({ ...BLANK_ASSET });
  const [saving, setSaving] = useState(false);
  const [recycled, setRecycled] = useState([]);
  const [showRecycleBin, setShowRecycleBin] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/breakdown`, { headers });
      const sceneData = res.data.map(s => ({
        ...s,
        artItems: [
          ...(s.breakdown_items?.PROP || []),
          ...(s.breakdown_items?.ART_SET_DRESSING || []),
          ...(s.breakdown_items?.PICTURE_VEHICLE || []),
        ],
      }));
      setScenes(sceneData);
      setAllScenes(sceneData);
      const rec = [];
      sceneData.forEach(s => s.artItems.filter(i => i.is_flagged).forEach(i => rec.push({ ...i, scene: s })));
      setRecycled(rec);
    } catch { toast.error('Failed to load art department data'); }
    finally { setLoading(false); }
  }, [projectId, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cycleStatus = (sceneId) => {
    const cur = artStatusMap[sceneId] || 'unreviewed';
    const idx = STATUS_CYCLE.indexOf(cur);
    const next = { ...artStatusMap, [sceneId]: STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length] };
    setArtStatusMap(next);
    try { localStorage.setItem(`art_status_${projectId}`, JSON.stringify(next)); } catch {}
  };

  const handleBulkAdd = async () => {
    const descriptions = bulkText.split(',').map(s => s.trim()).filter(Boolean);
    if (!descriptions.length) { toast.error('Add at least one item'); return; }
    if (!bulkSceneIds.length) { toast.error('No scene selected'); return; }
    setBulkSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/breakdown-items/bulk`, { scene_ids: bulkSceneIds, category: bulkCategory, descriptions }, { headers });
      toast.success(`Added ${descriptions.length} item(s)`);
      setShowBulkDialog(false); setBulkText(''); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk add failed'); }
    finally { setBulkSaving(false); }
  };

  const handleAddAsset = async () => {
    if (!newAsset.description.trim()) { toast.error('Asset name required'); return; }
    const sceneIds = newAsset.sceneIds.length > 0 ? newAsset.sceneIds : (dialogSceneId ? [dialogSceneId] : []);
    if (!sceneIds.length) { toast.error('Select at least one scene'); return; }
    setSaving(true);
    try {
      const breakdownCategory =
        newAsset.asset_sub_type === 'picture_vehicle' ? 'PICTURE_VEHICLE' :
        ['set_dressing', 'set_piece', 'decoration'].includes(newAsset.asset_sub_type) ? 'ART_SET_DRESSING' : 'PROP';
      await Promise.all(sceneIds.map(sid =>
        axios.post(`${API}/projects/${projectId}/breakdown-items`, { scene_id: sid, category: breakdownCategory, description: newAsset.description, asset_sub_type: newAsset.asset_sub_type, source: newAsset.source, notes: newAsset.notes }, { headers })
      ));
      toast.success(`Asset added to ${sceneIds.length} scene(s)`);
      setShowAssetDialog(false); setNewAsset({ ...BLANK_ASSET }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add asset'); }
    finally { setSaving(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await axios.delete(`${API}/breakdown-items/${itemId}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleFlagItem = async (itemId, flagged) => {
    try { await axios.patch(`${API}/breakdown-items/${itemId}`, { is_flagged: !flagged }, { headers }); fetchAll(); }
    catch { toast.error('Failed to update'); }
  };

  const totalProps       = scenes.reduce((a, s) => a + (s.breakdown_items?.PROP || []).length, 0);
  const totalSetDressing = scenes.reduce((a, s) => a + (s.breakdown_items?.ART_SET_DRESSING || []).length, 0);
  const totalVehicles    = scenes.reduce((a, s) => a + (s.breakdown_items?.PICTURE_VEHICLE || []).length, 0);
  const missingItems     = scenes.flatMap(s => (s.artItems || []).filter(i => i.is_flagged).map(i => ({ ...i, scene: s })));

  const openBulkFor = (scene) => {
    setBulkSceneIds([scene.id]); setBulkText(''); setBulkCategory('ART_SET_DRESSING'); setShowBulkDialog(true);
  };
  const openAssetFor = (scene) => {
    setDialogSceneId(scene.id); setNewAsset({ ...BLANK_ASSET, sceneIds: [scene.id] }); setShowAssetDialog(true);
  };
  const openPropFor = (scene) => {
    setDialogSceneId(scene.id); setNewAsset({ ...BLANK_ASSET, asset_sub_type: 'action_prop', sceneIds: [scene.id] }); setShowAssetDialog(true);
  };
  const openSetDressingFor = (scene) => {
    setDialogSceneId(scene.id); setNewAsset({ ...BLANK_ASSET, asset_sub_type: 'set_dressing', sceneIds: [scene.id] }); setShowAssetDialog(true);
  };
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [aiGenerateSceneId, setAIGenerateSceneId] = useState(null);
  const [aiGeneratePrompt, setAIGeneratePrompt] = useState('');
  const openAIGenerateFor = (scene) => {
    setAIGenerateSceneId(scene?.id || null);
    setAIGeneratePrompt('');
    setShowAIGenerate(true);
  };
  useEffect(() => {
    const handler = (e) => {
      setAIGenerateSceneId(e.detail?.sceneId || null);
      setAIGeneratePrompt(e.detail?.prompt || '');
      setShowAIGenerate(true);
    };
    window.addEventListener('art:ai-generate', handler);
    return () => window.removeEventListener('art:ai-generate', handler);
  }, []);

  // ── Art PDF Export — open the PDF Preview dialog ──────────────────────
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfLanguage, setPdfLanguage] = useState('english');
  const [showLangDialog, setShowLangDialog] = useState(false);
  const [projectLang, setProjectLang] = useState('english');

  // Fetch project language once
  useEffect(() => {
    if (!projectId || !token) return;
    axios.get(`${API}/projects/${projectId}/script-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const s = r.data || {};
        // /script-status returns the active script's breakdown_language directly.
        // Master Breakdown uses the same source (see MasterBreakdownPage line ~544).
        setProjectLang(s.breakdown_language || 'english');
      })
      .catch(() => setProjectLang('english'));
  }, [projectId, token]);

  const isEnglishProj = !projectLang || projectLang === 'english' || projectLang === 'en';

  const handleExportPdf = useCallback(() => {
    if (isEnglishProj) {
      setPdfLanguage('english');
      setPdfPreviewOpen(true);
    } else {
      setShowLangDialog(true);
    }
  }, [isEnglishProj]);

  const handleLangSelect = useCallback((lang) => {
    setShowLangDialog(false);
    setPdfLanguage(lang);
    setPdfPreviewOpen(true);
  }, []);

  // Phase 3 nav: 5-tab shell above the existing content. By Scene is
  // the new default; Assets renders the pre-existing sidebar UI intact.
  const [artTab, setArtTab] = useState(() => localStorage.getItem('art.top_tab') || 'by_scene');
  useEffect(() => { localStorage.setItem('art.top_tab', artTab); }, [artTab]);
  useEffect(() => {
    const onSwitch = (e) => {
      const nextTab = e?.detail?.tab;
      if (nextTab) setArtTab(nextTab);
    };
    window.addEventListener('art:switch-tab', onSwitch);
    return () => window.removeEventListener('art:switch-tab', onSwitch);
  }, []);
  const ART_TABS = [
    { key: 'by_scene',             label: 'By Scene' },
    { key: 'by_location',          label: 'By Location' },
    { key: 'by_character',         label: 'By Character' },
    { key: 'special_requirements', label: 'Special Requirements' },
    { key: 'assets',               label: 'Assets' },
  ];
  const TAB_DESC = {
    by_location:          'Base kits per location + scene overrides. Coming in Phase 5.',
    by_character:         'Signature props + costume/prop crossovers per character. Coming in Phase 8.',
  };

  const ART_EXPORT_OPTIONS_BY_TAB = {
    by_scene: [
      { key: 'scene_breakdown_all', type: 'scene_breakdown', label: 'Scene breakdown PDF (all scenes)' },
      { key: 'scene_look_all',      type: 'scene_look',      label: 'Scene look reference (all scenes)' },
      { key: 'shoot_day_bundle',    type: 'shoot_day_bundle', label: 'Shoot-day bundle (.zip)' },
    ],
    by_location: [
      { key: 'location_passport_all', type: 'location_passport', label: 'Location passport (all locations)' },
      { key: 'location_pack_all',     type: 'location_pack',     label: 'Location shooting pack (all locations)' },
    ],
    by_character: [
      { key: 'actor_bag_list_all', type: 'actor_bag_list', label: 'Actor bag list (all characters)' },
    ],
    special_requirements: [
      { key: 'requirements_sheet', type: 'requirements_sheet', label: 'Requirements sheet PDF' },
    ],
    assets: [
      { key: 'assets_inventory_pdf',  type: 'assets_inventory', label: 'Assets inventory PDF' },
      { key: 'assets_inventory_xlsx', type: 'assets_inventory', label: 'Assets inventory Excel', format: 'xlsx' },
    ],
  };

  const renderTopTabs = () => (
    <div className="flex border-b border-[var(--cb-border)] px-4 bg-[var(--cb-bg-secondary)]" data-testid="art-tabs">
      {ART_TABS.map(t => (
        <button key={t.key}
          onClick={() => setArtTab(t.key)}
          className={`px-4 py-2 text-xs font-semibold transition ${artTab === t.key ? 'text-[var(--cb-accent)] border-b-2 border-[var(--cb-accent)]' : 'text-[var(--cb-text-dim)] border-b-2 border-transparent hover:text-[var(--cb-text)]'}`}
          data-testid={`art-tab-${t.key.replace(/_/g,'-')}`}>
          {t.label}
        </button>
      ))}
      <div className="ml-auto flex items-center py-1.5">
        <ArtExportDropdown
          projectId={projectId} token={token}
          projectLanguage={projectLang}
          options={ART_EXPORT_OPTIONS_BY_TAB[artTab] || []}
        />
      </div>
    </div>
  );

  if (artTab === 'by_scene') {
    return (
      <AssetActionsProvider projectId={projectId} token={token}>
        <RequirementActionsProvider projectId={projectId} token={token}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)' }}>
          {renderTopTabs()}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ArtByScene projectId={projectId} token={token} />
          </div>
        </div>
        </RequirementActionsProvider>
      </AssetActionsProvider>
    );
  }
  if (artTab === 'by_location') {
    return (
      <AssetActionsProvider projectId={projectId} token={token}>
        <RequirementActionsProvider projectId={projectId} token={token}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)' }}>
          {renderTopTabs()}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ArtByLocation projectId={projectId} token={token} />
          </div>
        </div>
        </RequirementActionsProvider>
      </AssetActionsProvider>
    );
  }
  if (artTab === 'assets') {
    return (
      <AssetActionsProvider projectId={projectId} token={token}>
        <RequirementActionsProvider projectId={projectId} token={token}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)' }}>
          {renderTopTabs()}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ArtAssets projectId={projectId} token={token} />
          </div>
        </div>
        </RequirementActionsProvider>
      </AssetActionsProvider>
    );
  }
  if (artTab === 'by_character') {
    return (
      <AssetActionsProvider projectId={projectId} token={token}>
        <RequirementActionsProvider projectId={projectId} token={token}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)' }}>
          {renderTopTabs()}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ArtByCharacter projectId={projectId} token={token} />
          </div>
        </div>
        </RequirementActionsProvider>
      </AssetActionsProvider>
    );
  }
  if (artTab === 'special_requirements') {
    return (
      <AssetActionsProvider projectId={projectId} token={token}>
        <RequirementActionsProvider projectId={projectId} token={token}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)' }}>
          {renderTopTabs()}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ArtRequirements projectId={projectId} token={token} />
          </div>
        </div>
        </RequirementActionsProvider>
      </AssetActionsProvider>
    );
  }
  // 'assets' — render pre-existing sidebar UI intact (untouched).
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--cb-bg)', color: 'var(--cb-text)', overflow: 'hidden' }}>
      {renderTopTabs()}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <ArtSidebar section={section} setSection={setSection} onAddAsset={() => setShowAssetDialog(true)} onBulkAdd={() => { setBulkSceneIds([]); setShowBulkDialog(true); }} recycledCount={recycled.length} onRecycleBin={() => setShowRecycleBin(true)} onAIGenerate={() => openAIGenerateFor(null)} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ArtHeader scenes={scenes} totalProps={totalProps} totalSetDressing={totalSetDressing} totalVehicles={totalVehicles} section={section} setSection={setSection} onExport={() => {}} onExportPdf={handleExportPdf} />

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cb-text-dim)', fontSize: 13 }}>Loading art department data…</div>
        ) : (
          <>
            {section === 'overview' && (
              <OverviewSection scenes={scenes} statusMap={artStatusMap} missingItems={missingItems} setSection={setSection} />
            )}
            {section === 'scenes' && (
              <ScenesSection scenes={scenes} statusMap={artStatusMap} cycleStatus={cycleStatus} selectedScene={selectedScene} setSelectedScene={setSelectedScene} searchTerm={searchTerm} setSearchTerm={setSearchTerm} onBulkAdd={openBulkFor} onAddAsset={openAssetFor} onAddProp={openPropFor} onAddSetDressing={openSetDressingFor} onDelete={handleDeleteItem} fetchAll={fetchAll} token={token} projectId={projectId} onAIGenerate={openAIGenerateFor} />
            )}
            {section === 'locations' && (
              <LocationsSection scenes={scenes} token={token} fetchAll={fetchAll} projectId={projectId} />
            )}
            {section === 'gallery' && (
              <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                <LookAndFeelGallery projectId={projectId} department={DEPT} />
              </div>
            )}
            {section === 'tasks' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} data-testid="art-tasks-flagged-section">
                <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--cb-text)' }}>Flagged Items</h2>
                <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 16 }}>Items flagged during breakdown for the Art department.</p>
                {missingItems.length === 0
                  ? <p style={{ color: 'var(--cb-text-dim)', fontSize: 13 }}>No flagged items found.</p>
                  : missingItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', borderRadius: 8, marginBottom: 8 }}>
                      <AlertTriangle size={14} style={{ color: '#f87171', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: 'var(--cb-text)' }}><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                        <div style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginTop: 2 }}>Scene {item.scene?.scene_number} · {item.scene?.location_name || 'Unknown'}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleFlagItem(item.id, true)} className="text-green-400 text-xs h-7">Restore</Button>
                    </div>
                  ))}
                <div style={{ marginTop: 28 }} data-testid="art-tasks-department-section">
                  <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--cb-text)' }}>Department Tasks</h2>
                  <p style={{ fontSize: 11, color: 'var(--cb-text-dim)', marginBottom: 12 }}>Slate-style task list scoped to the Art department.</p>
                  <DepartmentTasksPanel projectId={projectId} department="ART" />
                </div>
              </div>
            )}
            {section === 'assets' && (
              <ArtAssetsSection scenes={scenes} onAIGenerate={() => openAIGenerateFor(null)} />
            )}
            {!['overview', 'scenes', 'locations', 'gallery', 'tasks', 'assets'].includes(section) && (
              <PlaceholderSection label={{ library: 'Set Dressing Library', continuity: 'Continuity', reports: 'Reports' }[section] || section} />
            )}
          </>
        )}
      </div>

      {/* ── Bulk Add Dialog ── */}
      <ArtAIGenerateModal
        open={showAIGenerate}
        onClose={() => setShowAIGenerate(false)}
        projectId={projectId}
        sceneId={aiGenerateSceneId}
        allScenes={scenes}
        token={token}
        onAssetAdded={() => fetchAll()}
        initialPrompt={aiGeneratePrompt}
      />
      <PDFPreviewDialog
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        pdfUrl={`${API}/projects/${projectId}/art/export-pdf?language=${pdfLanguage}`}
        fetchHeaders={headers}
        title="Art Department Breakdown — Preview"
        downloadName={`Art_Department_${projectId}_${pdfLanguage}.pdf`}
      />
      <LanguageDownloadDialog
        open={showLangDialog}
        projectLanguage={projectLang}
        onClose={() => setShowLangDialog(false)}
        onSelect={handleLangSelect}
      />
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Bulk Add Items</DialogTitle>
            <DialogDescription className="sr-only">Add multiple items to scenes in bulk</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[var(--cb-text-sec)]">Separate items with commas. You can edit and upload photos later.</p>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Category *</label>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="bulk-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
                  <SelectItem value="PROP">Action Prop</SelectItem>
                  <SelectItem value="ART_SET_DRESSING">Set Dressing</SelectItem>
                  <SelectItem value="PICTURE_VEHICLE">Picture Vehicle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Items (comma-separated) *</label>
              <Textarea value={bulkText} onChange={e => setBulkText(e.target.value)}
                placeholder="coffee mug, leather wallet, vintage camera, brass key"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[100px]" data-testid="bulk-text-input" />
              <p className="text-[11px] text-[var(--cb-text-sec)] mt-1">{bulkText.split(',').map(s => s.trim()).filter(Boolean).length} item(s)</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowBulkDialog(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleBulkAdd} disabled={bulkSaving} className="bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold" data-testid="bulk-add-save-btn">
                {bulkSaving ? 'Adding…' : 'Add Items'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add Asset Dialog ── */}
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-2xl">
          <DialogHeader><DialogTitle className="text-xl font-semibold">
            {['set_dressing', 'set_piece', 'decoration'].includes(newAsset.asset_sub_type) ? 'Add Set Dressing' : newAsset.asset_sub_type === 'picture_vehicle' ? 'Add Vehicle' : 'Add Prop'}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Asset Name *</label>
                <Input value={newAsset.description} onChange={e => setNewAsset(p => ({ ...p, description: e.target.value }))}
                  className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="asset-name-input" />
              </div>
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Category *</label>
                <Select value={newAsset.asset_sub_type} onValueChange={v => setNewAsset(p => ({ ...p, asset_sub_type: v }))}>
                  <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                    {ART_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-[var(--cb-text)]">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Notes</label>
              <Textarea value={newAsset.notes} onChange={e => setNewAsset(p => ({ ...p, notes: e.target.value }))}
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Source</label>
              <Select value={newAsset.source} onValueChange={v => setNewAsset(p => ({ ...p, source: v }))}>
                <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                  {SOURCES.map(s => <SelectItem key={s} value={s} className="text-[var(--cb-text)] capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-2 block">Used in Scenes</label>
              <div className="flex flex-wrap gap-2">
                {allScenes.map(scene => (
                  <button key={scene.id}
                    onClick={() => setNewAsset(p => ({ ...p, sceneIds: p.sceneIds.includes(scene.id) ? p.sceneIds.filter(s => s !== scene.id) : [...p.sceneIds, scene.id] }))}
                    className="h-9 w-9 rounded text-sm font-medium transition-colors"
                    style={{ background: newAsset.sceneIds.includes(scene.id) ? 'var(--cb-accent)' : 'var(--cb-border)', color: newAsset.sceneIds.includes(scene.id) ? 'var(--cb-bg)' : 'var(--cb-text-sec)' }}
                    data-testid={`asset-scene-${scene.scene_number ?? 'unnumbered'}`}>
                    {formatSceneNumber(scene.scene_number)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAssetDialog(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAddAsset} disabled={saving} className="bg-[var(--cb-accent)] hover:opacity-90 text-black font-semibold" data-testid="create-asset-btn">
                {saving ? 'Creating…' : 'Create Asset'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Recycle Bin Dialog ── */}
      <Dialog open={showRecycleBin} onOpenChange={setShowRecycleBin}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader><DialogTitle className="text-xl font-semibold">Recycle Bin</DialogTitle></DialogHeader>
          {recycled.length === 0
            ? <p className="text-[var(--cb-text-sec)] text-sm py-4 text-center">Recycle bin is empty</p>
            : <div className="space-y-2 max-h-80 overflow-y-auto">
              {recycled.map(item => (
                <div key={item.id} className="flex items-center justify-between bg-[var(--cb-bg)] p-3 rounded">
                  <div>
                    <div className="text-sm text-[var(--cb-text)]"><MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText></div>
                    <div className="text-xs text-[var(--cb-text-sec)]">Scene {item.scene?.scene_number}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleFlagItem(item.id, true)} className="text-green-400 hover:text-green-300 text-xs">Restore</Button>
                </div>
              ))}
            </div>}
        </DialogContent>
      </Dialog>
      </div>{/* /flex row (added Phase 3 for 5-tab shell) */}
    </div>
  );
}
